/**
 * @file CommandGraph.tsx
 * @description D3 force-directed "star" graph for one command's connections: the
 * command in the center, neighbours for the skills it fires, related commands, and
 * knowledge files — three edge colours. Clicking a related-command node navigates
 * to its profile. Adapted from workflows/AgentCollaborationNetwork (same d3 dep).
 */

import { useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import * as d3 from "d3";
import type { CommandDetail } from "../../lib/api";

type Kind = "center" | "skill" | "related" | "knowledge";

const KIND_COLOR: Record<Kind, string> = {
  center: "#9aa0aa",
  skill: "#1D9E75",
  related: "#378ADD",
  knowledge: "#BA7517",
};
const KIND_LABEL: Record<Exclude<Kind, "center">, string> = {
  skill: "skille",
  related: "pokrewne",
  knowledge: "wiedza",
};

// Pick near-black or white for a fill — whichever has higher WCAG contrast.
function readableOn(hex: string): string {
  const h = hex.replace("#", "");
  const ch = (i: number) => {
    const c = parseInt(h.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const lf = 0.2126 * ch(0) + 0.7152 * ch(2) + 0.0722 * ch(4);
  return (lf + 0.05) / 0.05 >= 1.05 / (lf + 0.05) ? "#0b0b12" : "#ffffff";
}

interface GNode extends d3.SimulationNodeDatum {
  id: string;
  label: string;
  kind: Kind;
  navId?: string; // related → command id to navigate to
}
interface GLink extends d3.SimulationLinkDatum<GNode> {
  kind: Kind;
}

export function CommandGraph({ command, color }: { command: CommandDetail; color: string }) {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const { nodes, links, isEmpty } = useMemo(() => {
    const c = command.connections;
    const nodes: GNode[] = [{ id: `c:${command.id}`, label: command.slash, kind: "center" }];
    const links: GLink[] = [];
    const push = (kind: Exclude<Kind, "center">, key: string, label: string, navId?: string) => {
      const id = `${kind}:${key}`;
      nodes.push({ id, label, kind, navId });
      links.push({ source: `c:${command.id}`, target: id, kind });
    };
    for (const s of c.skills) push("skill", s.id, s.label);
    for (const r of c.related) push("related", r.id, `/${r.id}`, r.id);
    for (const k of c.knowledge) push("knowledge", k.path, k.label);
    return { nodes, links, isEmpty: links.length === 0 };
  }, [command]);

  useEffect(() => {
    const svg = svgRef.current;
    const container = containerRef.current;
    if (!svg || !container || isEmpty) return;

    const width = container.clientWidth || 640;
    const height = Math.max(320, Math.min(460, Math.round(width * 0.55)));
    svg.setAttribute("viewBox", `0 0 ${width} ${height}`);
    svg.style.width = "100%";
    svg.style.height = `${height}px`;

    const root = d3.select(svg);
    root.selectAll("*").remove();

    const simNodes = nodes.map((n) => ({ ...n }));
    const byId = new Map(simNodes.map((n) => [n.id, n]));
    const simLinks: GLink[] = links.map((l) => ({
      ...l,
      source: byId.get(l.source as string) ?? (l.source as string),
      target: byId.get(l.target as string) ?? (l.target as string),
    }));

    const linkEls = root
      .append("g")
      .selectAll<SVGLineElement, GLink>("line")
      .data(simLinks)
      .join("line")
      .attr("stroke", (d) => KIND_COLOR[d.kind])
      .attr("stroke-opacity", 0.5)
      .attr("stroke-width", 1.5);

    const nodeEls = root
      .append("g")
      .selectAll<SVGGElement, GNode>("g")
      .data(simNodes, (d) => d.id)
      .join("g")
      .attr("cursor", (d) => (d.kind === "related" ? "pointer" : "grab"));

    // Transparent ~44px hit area for touch + drag/click (W2).
    nodeEls.append("circle").attr("r", 22).attr("fill", "transparent");

    nodeEls
      .append("circle")
      .attr("class", "viz")
      .attr("r", (d) => (d.kind === "center" ? 22 : 9))
      .attr("fill", (d) => (d.kind === "center" ? color : KIND_COLOR[d.kind]))
      .attr("fill-opacity", (d) => (d.kind === "center" ? 0.9 : 0.85))
      .attr("stroke", (d) => (d.kind === "center" ? color : KIND_COLOR[d.kind]))
      .attr("stroke-opacity", 0.9)
      .attr("stroke-width", (d) => (d.kind === "center" ? 2 : 1.5));

    nodeEls
      .append("text")
      .attr("text-anchor", "middle")
      .attr("dy", (d) => (d.kind === "center" ? 4 : 20))
      .attr("fill", (d) => (d.kind === "center" ? readableOn(color) : "#cbd5e1"))
      .attr("font-size", (d) => (d.kind === "center" ? "11px" : "10px"))
      .attr("font-weight", (d) => (d.kind === "center" ? "600" : "500"))
      .attr("font-family", "Inter, sans-serif")
      .attr("pointer-events", "none")
      .text((d) => (d.label.length > 22 ? d.label.slice(0, 20) + "…" : d.label));

    nodeEls.on("click", (_e: MouseEvent, d: GNode) => {
      if (d.kind === "related" && d.navId) navigate(`/komendy/${d.navId}`);
    });
    // Related nodes are keyboard-navigable buttons (W3) with a focus ring.
    nodeEls
      .filter((d) => d.kind === "related")
      .attr("tabindex", 0)
      .attr("role", "button")
      .attr("aria-label", (d) => `Przejdź do komendy ${d.label}`)
      .on("mouseenter", function () {
        d3.select(this).select("circle.viz").attr("stroke-width", 3);
      })
      .on("mouseleave", function () {
        d3.select(this).select("circle.viz").attr("stroke-width", 1.5);
      })
      .on("focus", function () {
        d3.select(this).select("circle.viz").attr("stroke", "#ffffff").attr("stroke-width", 3);
      })
      .on("blur", function (_event: FocusEvent, d: GNode) {
        d3.select(this)
          .select("circle.viz")
          .attr("stroke", KIND_COLOR[d.kind])
          .attr("stroke-width", 1.5);
      })
      .on("keydown", (event: KeyboardEvent, d: GNode) => {
        if ((event.key === "Enter" || event.key === " ") && d.navId) {
          event.preventDefault();
          navigate(`/komendy/${d.navId}`);
        }
      });

    const drag = d3
      .drag<SVGGElement, GNode>()
      .on("start", (event, d) => {
        if (!event.active) simulation.alphaTarget(0.2).restart();
        d.fx = d.x;
        d.fy = d.y;
      })
      .on("drag", (event, d) => {
        d.fx = event.x;
        d.fy = event.y;
      })
      .on("end", (event, d) => {
        if (!event.active) simulation.alphaTarget(0);
        d.fx = null;
        d.fy = null;
      });
    nodeEls.call(drag);

    const simulation = d3
      .forceSimulation<GNode>(simNodes)
      .force(
        "link",
        d3
          .forceLink<GNode, GLink>(simLinks)
          .id((d) => d.id)
          .distance(110)
      )
      .force("charge", d3.forceManyBody<GNode>().strength(-360))
      .force("center", d3.forceCenter(width / 2, height / 2))
      .force("collision", d3.forceCollide<GNode>().radius(34))
      .alpha(0.7)
      .on("tick", () => {
        for (const n of simNodes) {
          const r = 28;
          n.x = Math.max(r, Math.min(width - r, n.x ?? width / 2));
          n.y = Math.max(r, Math.min(height - r, n.y ?? height / 2));
        }
        linkEls
          .attr("x1", (d) => (d.source as GNode).x ?? 0)
          .attr("y1", (d) => (d.source as GNode).y ?? 0)
          .attr("x2", (d) => (d.target as GNode).x ?? 0)
          .attr("y2", (d) => (d.target as GNode).y ?? 0);
        nodeEls.attr("transform", (d) => `translate(${d.x ?? 0},${d.y ?? 0})`);
      });

    return () => {
      simulation.stop();
    };
  }, [nodes, links, isEmpty, color, navigate]);

  if (isEmpty) {
    return (
      <div className="card p-6 text-center text-xs text-gray-500">Brak zmapowanych połączeń.</div>
    );
  }

  return (
    <div ref={containerRef} className="card p-2 w-full">
      <svg
        ref={svgRef}
        style={{ display: "block", width: "100%", background: "transparent" }}
        role="group"
        aria-label={`Graf połączeń komendy ${command.slash} — węzły „pokrewne" są dostępne z klawiatury (Tab + Enter)`}
      />
      <div className="flex flex-wrap items-center gap-3 mt-1 px-2 pb-1">
        {(["skill", "related", "knowledge"] as const).map((k) => (
          <div key={k} className="flex items-center gap-1.5">
            <span
              className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: KIND_COLOR[k] }}
            />
            <span className="text-[11px] text-gray-400">{KIND_LABEL[k]}</span>
          </div>
        ))}
        <span className="text-[11px] text-gray-400 ml-1">klik w pokrewną → jej profil</span>
      </div>
    </div>
  );
}
